import React, { useLayoutEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import {
  marketingPreviewBootStorageKey,
  activateMarketingPreviewTabSession,
} from '../../utils/marketingPreviewStorage';

const handledBootIds = new Set();

/**
 * One-time handoff for landing-page iframe previews: consumes boot payload from localStorage,
 * activates demo JWT in this iframe tab, then navigates to the target in-app route.
 */
export default function MarketingPreviewBootstrap() {
  const { bootId } = useParams();
  const [error, setError] = useState(null);

  useLayoutEffect(() => {
    if (!bootId) {
      setError('Missing preview session id.');
      return;
    }
    if (handledBootIds.has(bootId)) return;

    const key = marketingPreviewBootStorageKey(bootId);
    const raw = localStorage.getItem(key);
    if (!raw) {
      setError('This preview link expired or was already used. Close the dialog and try again.');
      return;
    }

    try {
      const p = JSON.parse(raw);
      if (!p.token || !p.path) {
        setError('Invalid preview payload.');
        return;
      }
      handledBootIds.add(bootId);
      localStorage.removeItem(key);
      activateMarketingPreviewTabSession(p.token, p.refreshToken || '', { embed: true });
      const path = String(p.path).startsWith('/') ? p.path : `/${p.path}`;
      window.location.replace(path);
    } catch (e) {
      console.error('MarketingPreviewBootstrap', e);
      setError('Could not start preview session.');
    }
  }, [bootId]);

  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 480, mx: 'auto', mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Preview unavailable
        </Typography>
        <Typography color="text.secondary">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress size={36} sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Loading live demo screen…
        </Typography>
      </Box>
    </Box>
  );
}
